import React, { useState } from 'react';
import {
  Paper,
  TextInput,
  PasswordInput,
  Title,
  Text,
  Divider,
  Stack,
  Tabs,
  Alert
} from '@mantine/core';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PrimaryButton, SecondaryButton } from '../UI';
import { safeRedirectPath } from '../../lib/safeRedirect';
import { PASSWORD_HINT, passwordProblem } from '../../lib/passwordRules';

const inputStyles = {
  root: { textAlign: 'left' },
  label: { fontWeight: 600, color: 'var(--charcoal)', textAlign: 'left', display: 'block', width: '100%' }
};

// PasswordInput's inner <input> element needs extra right padding so long placeholder/typed values
// don't run under the eye toggle icon. Targeting `innerInput` (the real input) instead of `input`
// (the wrapper) avoids disturbing Mantine's flex layout for the rightSection.
const passwordInputStyles = {
  ...inputStyles,
  innerInput: { paddingRight: 36 }
};

const AuthForm = ({ defaultTab = 'login', title, subtitle }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const { signIn, signUp, signInWithGoogle, signInWithFacebook, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get redirect URL from query params, default to /profile
  const redirectTo = safeRedirectPath(searchParams.get('redirect'));

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error } = await resetPassword(resetEmail);
      if (error) throw error;

      // Reminder only: the email still goes out, but an account with no
      // password (Google-only) can simply use the Google button instead.
      let reminder = '';
      try {
        const conflictRes = await fetch('/api/check-email-conflict', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resetEmail })
        });
        if (conflictRes.ok) {
          const conflict = await conflictRes.json();
          if (conflict.exists && !conflict.providers.includes('email')) {
            const otherLabel = conflict.providers.map(providerLabel).join(' or ');
            reminder = ` Reminder: this email is registered with ${otherLabel}, so you can also just use the "Continue with ${otherLabel}" button.`;
          }
        }
      } catch (checkErr) {
        console.warn('Email provider check failed:', checkErr);
      }

      setSuccess(`✅ Password reset email sent! Please check your inbox (and spam folder), and open the link on this device, in this browser.${reminder}`);
      setResetEmail('');
      setShowForgotPassword(false);
    } catch (error) {
      setError(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  // Human-friendly label for a Supabase provider string
  const providerLabel = (p) => {
    if (p === 'email') return 'email and password';
    if (p === 'google') return 'Google';
    if (p === 'facebook') return 'Facebook';
    return p;
  };

  // Message shown when a signup attempt collides with an existing account
  const buildConflictMessage = (providers) => {
    if (!providers || providers.length === 0) return 'An account with this email already exists. Please sign in instead.';
    if (providers.length === 1) {
      return `An account with this email already exists — please sign in with ${providerLabel(providers[0])} instead.`;
    }
    const readable = providers.map(providerLabel).join(' or ');
    return `An account with this email already exists — please sign in with ${readable} instead.`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (activeTab === 'signup') {
        // Validation
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        const problem = passwordProblem(password);
        if (problem) {
          setError(problem);
          setLoading(false);
          return;
        }

        // Guard against duplicate accounts across providers.
        // If this email already exists (via password OR Google OR anything),
        // point the user to the right sign-in method instead of creating a duplicate.
        try {
          const conflictRes = await fetch('/api/check-email-conflict', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          if (conflictRes.ok) {
            const conflict = await conflictRes.json();
            if (conflict.exists) {
              setError(buildConflictMessage(conflict.providers));
              setLoading(false);
              return;
            }
          }
          // On non-OK response we intentionally fall through to normal signup — worst case
          // we get a Supabase duplicate error, which the catch below handles.
        } catch (checkErr) {
          console.warn('Email conflict check failed, proceeding with signup:', checkErr);
        }

        const { error } = await signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/profile`
          }
        });

        if (error) throw error;

        setSuccess('✅ Confirmation email sent! Please check your inbox (and spam folder) to verify your PDC account. You must verify your email before signing in.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
      } else {
        const { error } = await signIn({ email, password });

        if (error) {
          // If credentials failed, this email might actually be registered via
          // a different provider (Google/Facebook) with no password at all.
          // Give a specific pointer instead of the vague "Invalid credentials".
          try {
            const conflictRes = await fetch('/api/check-email-conflict', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email })
            });
            if (conflictRes.ok) {
              const conflict = await conflictRes.json();
              if (conflict.exists && !conflict.providers.includes('email')) {
                const otherLabel = conflict.providers.map(providerLabel).join(' or ');
                setError(`This email is registered with ${otherLabel} — please use the "Continue with ${otherLabel}" button above instead of a password.`);
                setLoading(false);
                return;
              }
            }
          } catch (checkErr) {
            console.warn('Login conflict check failed:', checkErr);
          }
          throw error;
        }

        navigate(redirectTo);
      }
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider) => {
    setError('');
    try {
      // Store redirect path in localStorage before OAuth redirect
      if (redirectTo && redirectTo !== '/profile') {
        localStorage.setItem('oauth_redirect', redirectTo);
      }

      const { error } = provider === 'google'
        ? await signInWithGoogle()
        : await signInWithFacebook();

      if (error) throw error;
    } catch (error) {
      setError(error.message || 'OAuth sign in failed');
    }
  };

  return (
    <Paper
      radius="md"
      p={{ base: 'md', sm: 'xl' }}
      withBorder
      style={{
        maxWidth: '500px',
        margin: '0 auto',
        borderTop: '4px solid var(--primary-gold)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
      }}
    >
      {title && (
        <Title
          order={2}
          style={{
            fontFamily: "'Playfair Display', 'Georgia', serif",
            textAlign: 'center',
            marginBottom: '0.5rem',
            color: 'var(--charcoal)'
          }}
        >
          {title}
        </Title>
      )}

      {subtitle && (
        <Text
          size="md"
          style={{
            textAlign: 'center',
            marginBottom: '2rem',
            color: 'var(--soft-gray)'
          }}
        >
          {subtitle}
        </Text>
      )}

      {showForgotPassword ? (
        <>
          <Text
            size="sm"
            style={{ textAlign: 'left', cursor: 'pointer', color: 'var(--primary-gold)', marginBottom: '1rem' }}
            onClick={() => {
              setShowForgotPassword(false);
              setError('');
              setSuccess('');
            }}
          >
            ← Back to Sign In
          </Text>
          <Title order={3} mb="md" style={{ textAlign: 'left', color: 'var(--charcoal)' }}>
            Reset Your Password
          </Title>
          <Text size="sm" color="dimmed" mb="xl" style={{ textAlign: 'left' }}>
            Enter your email address and we'll send you instructions to reset your password.
          </Text>
          <form onSubmit={handleForgotPassword}>
            <Stack gap="md">
              <TextInput
                required
                label="Email"
                placeholder="your@email.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                styles={inputStyles}
              />
              <PrimaryButton type="submit" fullWidth loading={loading}>
                Send Reset Instructions
              </PrimaryButton>
            </Stack>
          </form>
        </>
      ) : (
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List grow>
          <Tabs.Tab
            value="login"
            style={{
              fontWeight: activeTab === 'login' ? 600 : 400,
              color: activeTab === 'login' ? 'var(--primary-gold)' : 'var(--soft-gray)'
            }}
          >
            Sign In
          </Tabs.Tab>
          <Tabs.Tab
            value="signup"
            style={{
              fontWeight: activeTab === 'signup' ? 600 : 400,
              color: activeTab === 'signup' ? 'var(--primary-gold)' : 'var(--soft-gray)'
            }}
          >
            Sign Up
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="login" pt="xl">
          {/* Google OAuth Button */}
          <PrimaryButton
            onClick={() => handleOAuthSignIn('google')}
            fullWidth
            size="lg"
            mb="lg"
            leftSection={
              <svg width="24" height="24" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
                <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
              </svg>
            }
          >
            Continue with Google
          </PrimaryButton>

          <Divider
            label="or"
            labelPosition="center"
            style={{ marginBottom: '1.5rem' }}
          />

          <form onSubmit={handleEmailAuth}>
            <Stack gap="md">
              <TextInput
                required
                label="Email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                styles={inputStyles}
              />

              <PasswordInput
                required
                label="Password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                styles={passwordInputStyles}
              />

              <Text
                size="sm"
                style={{ textAlign: 'right', cursor: 'pointer', color: 'var(--primary-gold)', marginTop: '-0.5rem' }}
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                  setSuccess('');
                }}
              >
                Forgot your password?
              </Text>

              <SecondaryButton type="submit" fullWidth loading={loading} size="lg" mt="xs">
                Sign In
              </SecondaryButton>
            </Stack>
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="signup" pt="xl">
          {/* Google OAuth Button */}
          <PrimaryButton
            onClick={() => handleOAuthSignIn('google')}
            fullWidth
            size="lg"
            mb="lg"
            leftSection={
              <svg width="24" height="24" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
                <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
              </svg>
            }
          >
            Continue with Google
          </PrimaryButton>

          <Divider
            label="or"
            labelPosition="center"
            style={{ marginBottom: '1.5rem' }}
          />

          <form onSubmit={handleEmailAuth}>
            <Stack gap="md">
              <TextInput
                required
                label="Email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                styles={inputStyles}
              />

              <PasswordInput
                required
                label="Password"
                placeholder={PASSWORD_HINT}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                styles={passwordInputStyles}
              />

              <PasswordInput
                required
                label="Confirm Password"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                styles={passwordInputStyles}
              />

              <SecondaryButton type="submit" fullWidth loading={loading} size="lg" mt="xs">
                Create Account
              </SecondaryButton>

              <Text size="xs" style={{ color: 'var(--soft-gray)', textAlign: 'center', marginTop: '-0.5rem' }}>
                You'll receive a confirmation email
              </Text>
            </Stack>
          </form>
        </Tabs.Panel>
      </Tabs>
      )}

      {error && (
        <Alert color="red" style={{ marginTop: '1rem' }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="green" style={{ marginTop: '1rem' }}>
          {success}
        </Alert>
      )}
    </Paper>
  );
};

export default AuthForm;
